import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import { IdCard, Loader2, Bluetooth, Clock3, AlertTriangle, CheckCircle2, CircleHelp, XCircle, UserX } from 'lucide-react';
import { formatDT } from '../../utils/timezone';
import { AllDeclinedDriverRecovery } from './AllDeclinedDriverRecovery';

const formatTripDate = (value) => {
  if (!value) return '—';
  return formatDT(value, { seconds: true });
};

const tripStatusLabel = (candidate) => (candidate?.trip_ended_at || candidate?.trip_status === 'completed' ? 'Completed' : 'Ongoing');

// Manager-side "who is driving?" confirmation for an ambiguous BLE driver candidate (Phase 3B).
export const DriverConfirmDialog = ({ api, candidateId, open, onOpenChange, onResolved }) => {
  const [candidate, setCandidate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(null);
  const [closedMessage, setClosedMessage] = useState(null);
  const [overrideTarget, setOverrideTarget] = useState(null);
  const [overrideReason, setOverrideReason] = useState('');
  const [noDriverMode, setNoDriverMode] = useState(false);
  const [noDriverReason, setNoDriverReason] = useState('');

  useEffect(() => {
    if (!open || !candidateId || !api) return;
    let alive = true;
    (async () => {
      setLoading(true);
      setClosedMessage(null);
      setOverrideTarget(null);
      setOverrideReason('');
      setNoDriverMode(false);
      setNoDriverReason('');
      try {
        const res = await api.get(`/fleet/driver-candidates/${candidateId}`);
        if (!alive) return;
        const found = res.data;
        const actionable = found?.status === 'pending'
          || (found?.status === 'no_driver' && found?.manager_review_required === true)
          || (found?.status === 'confirmed' && found?.resolution?.reason === 'last_remaining_after_rejections');
        if (!found || !actionable) {
          setClosedMessage('This confirmation has already been answered or has expired.');
          setCandidate(found || null);
        } else {
          setCandidate(found);
        }
      } catch (error) {
        if (alive) {
          setClosedMessage(error?.response?.data?.detail || 'Could not load this Driver confirmation.');
          setCandidate(null);
        }
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [open, candidateId, api]);

  const confirmDriver = async (personId, overrideDeclined = false) => {
    const reason = overrideDeclined ? overrideReason.trim() : 'manager_confirmation';
    if (overrideDeclined && !reason) {
      toast.error('Enter a reason for overriding the declined response');
      return;
    }
    setSubmitting(personId);
    try {
      const res = await api.post(`/fleet/driver-candidates/${candidateId}/resolve`, {
        driver_person_id: personId,
        reason,
        override_declined_response: overrideDeclined,
      });
      toast.success(`Driver confirmed: ${res.data?.resolution?.person_name || ''}`);
      onResolved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to confirm driver');
    } finally {
      setSubmitting(null);
    }
  };

  const confirmNoDriver = async () => {
    if (autoCorrectable && !noDriverReason.trim()) {
      toast.error('Enter a reason for correcting the automatic Driver assignment');
      return;
    }
    setSubmitting('no-driver');
    try {
      await api.post(`/fleet/driver-candidates/${candidateId}/resolve-no-driver`, { reason: noDriverReason.trim() || null });
      toast.success('No Driver confirmed');
      onResolved?.();
      onOpenChange(false);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to confirm No Driver');
    } finally {
      setSubmitting(null);
    }
  };

  const responseMeta = (entry) => {
    if (entry.response === 'rejected') {
      return { label: 'Declined', icon: XCircle, className: 'border-red-300 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-950/40 dark:text-red-300' };
    }
    if (entry.response === 'accepted') {
      return { label: 'Accepted', icon: CheckCircle2, className: 'border-emerald-300 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300' };
    }
    return { label: 'No response', icon: CircleHelp, className: 'border-slate-300 bg-slate-50 text-slate-600 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300' };
  };

  const allDeclined = !!candidate?.candidates?.length && candidate.candidates.every((entry) => entry.response === 'rejected');
  const autoCorrectable = candidate?.status === 'confirmed' && candidate?.resolution?.reason === 'last_remaining_after_rejections';
  const currentDriverId = autoCorrectable ? candidate?.resolution?.driver_person_id : null;

  const candidateCount = candidate?.candidates?.length || 0;
  const zeroCandidates = !!candidate && candidateCount === 0;
  const singleCandidate = !!candidate && candidateCount === 1;
  const multipleCandidates = candidateCount >= 2;
  const headerTitle = zeroCandidates && !allDeclined && !autoCorrectable ? 'Driver review required' : 'Confirm Driver';
  const headerDescription = !candidate
    ? 'Select who is driving this vehicle.'
    : allDeclined
      ? 'Everyone declined for this trip. Review the original candidates, choose someone missed by BLE, or confirm No Driver.'
      : autoCorrectable
        ? `${candidate.resolution?.driver_person_name || 'The last remaining candidate'} was automatically assigned. Keep this Driver or make a reasoned correction.`
        : zeroCandidates
          ? 'No Driver beacon was detected for this trip. Assign a Driver from People or confirm No Driver.'
          : singleCandidate
            ? 'One eligible person was detected. Confirm who was driving.'
            : 'Two or more eligible people were detected. Select who is driving.';
  const stateTestId = !candidate
    ? undefined
    : zeroCandidates
      ? 'zero-candidate-state'
      : singleCandidate
        ? 'single-candidate-state'
        : multipleCandidates
          ? 'multi-candidate-state'
          : undefined;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-md overflow-y-auto" data-testid="driver-confirm-dialog">
        <DialogHeader className="flex flex-col space-y-2 text-left pb-3 border-b border-border/40">
          <DialogTitle className="flex items-center gap-2 text-xl sm:text-2xl font-semibold tracking-tight" data-testid="driver-confirm-title">
            <IdCard className="w-5 h-5 shrink-0" /> <span data-testid="driver-review-dialog-title">{headerTitle}</span>
          </DialogTitle>
          {candidate && (
            <div className="w-full rounded-lg border border-border/50 bg-muted/40 shadow-sm px-4 py-3 my-1" data-testid="prominent-vehicle-name">
              <span className="block text-2xl sm:text-3xl font-bold text-primary break-words min-w-0">
                {candidate.vehicle_name || 'Vehicle'}
              </span>
            </div>
          )}
          <DialogDescription className="text-sm text-muted-foreground leading-relaxed" data-testid="driver-confirm-description">
            <span data-testid="driver-review-dialog-desc">
              {stateTestId ? <span data-testid={stateTestId}>{headerDescription}</span> : headerDescription}
            </span>
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8" data-testid="driver-confirm-loading"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
        ) : closedMessage ? (
          <p className="text-sm text-muted-foreground py-4" data-testid="driver-confirm-resolved">
            {closedMessage}
          </p>
        ) : !candidate ? (
          <p className="text-sm text-muted-foreground py-4" data-testid="driver-confirm-resolved-empty">
            This confirmation has already been answered or has expired.
          </p>
        ) : (
          <div className="space-y-3 py-1">
            {allDeclined && (
              <div className="flex gap-2 rounded-md border border-amber-300 bg-amber-50 p-2.5 text-xs text-amber-950 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-100" data-testid="driver-all-declined-review-status">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                <p><strong>No Driver — Manager Review Required.</strong> This review remains available until you resolve it.</p>
              </div>
            )}
            {autoCorrectable && (
              <div className="flex gap-2 rounded-md border border-emerald-300 bg-emerald-50 p-2.5 text-xs text-emerald-950 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-100" data-testid="driver-auto-assignment-status">
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
                <p><strong>Automatically assigned:</strong> {candidate.candidates?.find((entry) => entry.person_id === currentDriverId)?.person_name || 'Last remaining candidate'}. No action is required unless this is incorrect.</p>
              </div>
            )}
            <div className="rounded-lg border bg-muted/30 px-3 py-2 text-xs" data-testid="driver-confirm-trip-context">
              <div className="flex items-center justify-between gap-2">
                <span className="font-semibold flex items-center gap-1"><Clock3 className="w-3.5 h-3.5" />{tripStatusLabel(candidate)} trip</span>
                {candidate.ballot_sent_at && <span className="text-muted-foreground">Ballot sent {formatTripDate(candidate.ballot_sent_at)}</span>}
              </div>
              <p className="text-muted-foreground mt-1">
                Start {formatTripDate(candidate.trip_started_at || candidate.started_at)}
                {candidate.trip_ended_at ? ` · End ${formatTripDate(candidate.trip_ended_at)}` : ' · Still ongoing'}
              </p>
            </div>
            {(candidate.candidates || []).map((c) => {
              const response = responseMeta(c);
              const ResponseIcon = response.icon;
              const declined = c.response === 'rejected';
              return (
                <div key={c.person_id} className={`rounded-lg border p-2.5 ${declined ? 'border-red-200 bg-red-50/40 dark:border-red-900 dark:bg-red-950/20' : ''}`} data-testid={`driver-candidate-${c.person_id}`}>
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-1.5">
                        <p className="text-sm font-semibold truncate" data-testid={`driver-candidate-name-${c.person_id}`}>{c.person_name}</p>
                        <Badge variant="outline" className={`text-[9px] px-1.5 py-0 ${response.className}`} data-testid={`driver-candidate-response-${c.person_id}`}>
                          <ResponseIcon className="w-3 h-3 mr-1" />{response.label}
                        </Badge>
                      </div>
                      <div className="text-[11px] text-muted-foreground flex items-center gap-1.5">
                        <Badge variant="outline" className="text-[9px] px-1 py-0 capitalize">{c.person_type}</Badge>
                        <span className="flex items-center gap-0.5"><Bluetooth className="w-3 h-3" />{c.median_rssi != null ? `${c.median_rssi} dBm` : '—'}</span>
                        {c.score != null && <span>· score {c.score}</span>}
                      </div>
                      {declined && c.responded_at && <p className="mt-1 text-[10px] text-red-700 dark:text-red-300" data-testid={`driver-candidate-declined-at-${c.person_id}`}>Declined {formatTripDate(c.responded_at)}</p>}
                    </div>
                    {c.person_id === currentDriverId ? (
                      <Badge className="shrink-0 bg-emerald-700 text-white" data-testid={`driver-candidate-current-${c.person_id}`}>Current Driver</Badge>
                    ) : declined ? (
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-8 shrink-0 border-amber-400 text-amber-800 hover:bg-amber-50 dark:text-amber-300"
                        disabled={!!submitting}
                        onClick={() => { setOverrideTarget(c); setOverrideReason(''); }}
                        data-testid={`driver-candidate-override-${c.person_id}`}
                      >
                        <AlertTriangle className="w-3.5 h-3.5" />Override
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        className="h-8 shrink-0"
                        disabled={!!submitting}
                        onClick={() => confirmDriver(c.person_id)}
                        data-testid={`driver-candidate-confirm-${c.person_id}`}
                      >
                        {submitting === c.person_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'This driver'}
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
            {overrideTarget && (
              <div className="space-y-3 border-t border-amber-300 pt-3" data-testid="driver-declined-override-confirmation">
                <div className="flex gap-2 rounded-md bg-amber-50 p-2.5 text-xs text-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
                  <AlertTriangle className="mt-0.5 w-4 h-4 shrink-0" />
                  <p><strong>{overrideTarget.person_name}</strong> said they were not driving. This override will be audited and they will be notified.</p>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="driver-declined-override-reason">Override reason</Label>
                  <Textarea
                    id="driver-declined-override-reason"
                    value={overrideReason}
                    onChange={(event) => setOverrideReason(event.target.value)}
                    placeholder="How was the correct driver confirmed?"
                    maxLength={500}
                    data-testid="driver-declined-override-reason"
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" size="sm" onClick={() => { setOverrideTarget(null); setOverrideReason(''); }} disabled={!!submitting} data-testid="driver-declined-override-cancel">Cancel</Button>
                  <Button size="sm" className="bg-amber-700 text-white hover:bg-amber-800" onClick={() => confirmDriver(overrideTarget.person_id, true)} disabled={!!submitting || !overrideReason.trim()} data-testid="driver-declined-override-confirm">
                    {submitting === overrideTarget.person_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Confirm override'}
                  </Button>
                </div>
              </div>
            )}
            <AllDeclinedDriverRecovery
              api={api}
              candidateId={candidateId}
              disabled={!!submitting}
              onResolved={() => { onResolved?.(); onOpenChange(false); }}
            />
            <div className="space-y-3 border-t pt-3" data-testid="driver-no-driver-section">
              {!noDriverMode ? (
                <Button variant="outline" className="w-full border-slate-400" onClick={() => { setNoDriverMode(true); setOverrideTarget(null); }} disabled={!!submitting} data-testid="driver-confirm-no-driver-button">
                  <UserX className="h-4 w-4" />Confirm No Driver
                </Button>
              ) : (
                <div className="space-y-2" data-testid="driver-no-driver-confirmation">
                  <p className="text-xs text-muted-foreground">Use this when you cannot confidently identify the Driver. This closes the ballot without an assignment.</p>
                  <Label htmlFor="driver-no-driver-reason">{autoCorrectable ? 'Correction reason' : 'Note (optional)'}</Label>
                  <Textarea id="driver-no-driver-reason" value={noDriverReason} onChange={(event) => setNoDriverReason(event.target.value)} maxLength={500} placeholder="Why could the Driver not be confirmed?" data-testid="driver-no-driver-reason" />
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" size="sm" onClick={() => { setNoDriverMode(false); setNoDriverReason(''); }} disabled={!!submitting} data-testid="driver-no-driver-cancel">Cancel</Button>
                    <Button size="sm" variant="destructive" onClick={confirmNoDriver} disabled={!!submitting || (autoCorrectable && !noDriverReason.trim())} data-testid="driver-no-driver-confirm">
                      {submitting === 'no-driver' ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserX className="h-4 w-4" />}Confirm No Driver
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DriverConfirmDialog;
